import { useCallback, useMemo } from 'react';
import { Platform, Alert } from 'react-native';
import { useModalStore } from '@/store';

export const useModalHelper = () => {
  // Dialog visibility is irrelevant to callers that only need its actions.
  const { openModal, closeModal } = useModalStore(state => state.actions);

  const showInputModal = useCallback(({
    title,
    subtitle,
    placeholder,
    primaryButtonTitle = 'Save',
    initialValue = '',
    onSave,
    onCancel,
    ...props
  }) => {
    if (Platform.OS === 'web') {
      openModal('InputModal', {
        title,
        subtitle,
        placeholder,
        primaryButtonTitle,
        initialValue,
        onSave: (value) => {
          closeModal();
          onSave?.(value);
        },
        onCancel: () => {
          closeModal();
          onCancel?.();
        },
        ...props
      });
    } else if (Platform.OS === 'ios') {
      // iOS uses Prompt
      Alert.prompt(
        title,
        subtitle,
        [
          {
            text: 'Cancel',
            onPress: () => onCancel?.(),
            style: 'cancel'
          },
          {
            text: primaryButtonTitle,
            onPress: (value) => onSave?.(value)
          }
        ],
        props.type === 'password' ? 'secure-text' : 'plain-text',
        initialValue,
        'default',
        'default',
        placeholder
      );
    } else {
      // Android doesn't have a built-in prompt, so we use Alert with input modal
      openModal('InputModal', {
        title,
        subtitle,
        placeholder,
        primaryButtonTitle,
        initialValue,
        onSave: (value) => {
          closeModal();
          onSave?.(value);
        },
        onCancel: () => {
          closeModal();
          onCancel?.();
        },
        ...props
      });
    }
  }, [openModal, closeModal]);

  const showConfirmationModal = useCallback(({
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    onConfirm,
    onCancel,
    isDangerous = false,
    options = {},
    ...props
  }) => {
    if (Platform.OS === 'web') {
      openModal('ConfirmationModal', {
        title,
        message,
        confirmText,
        cancelText,
        onConfirm: () => {
          closeModal();
          onConfirm?.();
        },
        onCancel: () => {
          closeModal();
          onCancel?.();
        },
        isDanger: isDangerous,
        ...props
      }, {
        ...options
      });
    } else {
      // Both iOS and Android can use Alert for confirmation
      Alert.alert(
        title,
        message,
        [
          {
            text: cancelText,
            onPress: () => onCancel?.(),
            style: 'cancel'
          },
          {
            text: confirmText,
            onPress: () => onConfirm?.(),
            style: isDangerous ? 'destructive' : 'default'
          }
        ],
        { cancelable: false }
      );
    }
  }, [openModal, closeModal]);

  // Keep the existing factory API and the returned callbacks stable so callers
  // can safely use them in memoized markdown rules and event handlers.
  return useMemo(() => ({
    useInputModal: () => showInputModal,
    useConfirmationModal: () => showConfirmationModal,
  }), [showInputModal, showConfirmationModal]);
};

export default useModalHelper;
